import { useState, useEffect } from 'react';
import { API } from '@/App';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Star, MessageSquare, Music, Wine, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

const StarRating = ({ value, onChange, size = 'md', readonly = false }) => {
  const [hover, setHover] = useState(0);
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8'
  };

  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={readonly}
          onClick={() => !readonly && onChange?.(star)}
          onMouseEnter={() => !readonly && setHover(star)}
          onMouseLeave={() => !readonly && setHover(0)}
          className={`${readonly ? 'cursor-default' : 'cursor-pointer'} transition-transform ${!readonly && 'hover:scale-110'}`}
        >
          <Star
            className={`${sizeClasses[size]} ${
              (hover || value) >= star
                ? 'text-yellow-400 fill-yellow-400'
                : 'text-slate-600'
            }`}
          />
        </button>
      ))}
    </div>
  );
};

const BarRatings = ({ barId }) => {
  const [ratingsData, setRatingsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    rating: 0,
    review: '',
    atmosphere_rating: 0,
    drinks_rating: 0,
    music_rating: 0
  });

  const fetchRatings = async () => {
    try {
      const response = await axios.get(`${API}/bars/${barId}/ratings`);
      setRatingsData(response.data);
    } catch (e) {
      console.error('Failed to fetch ratings:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRatings();
  }, [barId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.rating === 0) {
      toast.error('Please select a rating');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/bars/${barId}/rate`, formData);
      toast.success(response.data.message);
      if (response.data.points_earned) {
        toast.success(`+${response.data.points_earned} points earned!`, { icon: '🎉' });
      }
      setIsDialogOpen(false);
      setFormData({ rating: 0, review: '', atmosphere_rating: 0, drinks_rating: 0, music_rating: 0 });
      fetchRatings();
    } catch (e) {
      console.error('Failed to submit rating:', e);
      toast.error('Failed to submit rating');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Card className="glass p-6 border border-slate-700">
        <div className="animate-pulse">
          <div className="h-6 bg-slate-700 rounded w-1/3 mb-4" />
          <div className="h-20 bg-slate-700 rounded" />
        </div>
      </Card>
    );
  }

  return (
    <Card className="glass p-6 border border-slate-700 fade-in">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-semibold text-white flex items-center gap-2">
          <Star className="w-5 h-5 text-yellow-400" />
          Ratings & Reviews
        </h3>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button
              size="sm"
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="write-review-btn"
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              Write Review
            </Button>
          </DialogTrigger>
          <DialogContent className="glass border border-slate-700 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white">Rate This Bar</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Overall Rating */}
              <div>
                <label className="text-sm text-slate-300 mb-2 block">Overall Rating *</label>
                <StarRating
                  value={formData.rating}
                  onChange={(val) => setFormData({ ...formData, rating: val })}
                  size="lg"
                />
              </div>

              {/* Category Ratings */}
              <div className="grid grid-cols-3 gap-3">
                <div className="text-center">
                  <Sparkles className="w-5 h-5 text-purple-400 mx-auto mb-1" />
                  <p className="text-xs text-slate-400 mb-1">Atmosphere</p>
                  <StarRating
                    value={formData.atmosphere_rating}
                    onChange={(val) => setFormData({ ...formData, atmosphere_rating: val })}
                    size="sm"
                  />
                </div>
                <div className="text-center">
                  <Wine className="w-5 h-5 text-pink-400 mx-auto mb-1" />
                  <p className="text-xs text-slate-400 mb-1">Drinks</p>
                  <StarRating
                    value={formData.drinks_rating}
                    onChange={(val) => setFormData({ ...formData, drinks_rating: val })}
                    size="sm"
                  />
                </div>
                <div className="text-center">
                  <Music className="w-5 h-5 text-blue-400 mx-auto mb-1" />
                  <p className="text-xs text-slate-400 mb-1">Music</p>
                  <StarRating
                    value={formData.music_rating}
                    onChange={(val) => setFormData({ ...formData, music_rating: val })}
                    size="sm"
                  />
                </div>
              </div>

              {/* Review Text */}
              <div>
                <label className="text-sm text-slate-300 mb-2 block">Your Review (optional)</label>
                <Textarea
                  value={formData.review}
                  onChange={(e) => setFormData({ ...formData, review: e.target.value })}
                  placeholder="Share your experience..."
                  className="bg-slate-800 border-slate-700 text-white"
                  rows={3}
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-purple-600 hover:bg-purple-700"
                disabled={submitting}
              >
                {submitting ? 'Submitting...' : 'Submit Review'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Average Ratings Display */}
      {ratingsData && ratingsData.total_ratings > 0 && (
        <div className="bg-slate-800/50 rounded-lg p-4 mb-4">
          <div className="flex items-center gap-4 mb-3">
            <div className="text-4xl font-bold text-yellow-400">{ratingsData.average_rating}</div>
            <div>
              <StarRating value={ratingsData.average_rating} readonly size="md" />
              <p className="text-sm text-slate-400 mt-1">{ratingsData.total_ratings} reviews</p>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2 text-center text-xs">
            <div className="p-2 bg-slate-700/50 rounded">
              <Sparkles className="w-4 h-4 text-purple-400 mx-auto mb-1" />
              <p className="text-slate-400">Atmosphere</p>
              <p className="text-white font-semibold">{ratingsData.average_atmosphere || 'N/A'}</p>
            </div>
            <div className="p-2 bg-slate-700/50 rounded">
              <Wine className="w-4 h-4 text-pink-400 mx-auto mb-1" />
              <p className="text-slate-400">Drinks</p>
              <p className="text-white font-semibold">{ratingsData.average_drinks || 'N/A'}</p>
            </div>
            <div className="p-2 bg-slate-700/50 rounded">
              <Music className="w-4 h-4 text-blue-400 mx-auto mb-1" />
              <p className="text-slate-400">Music</p>
              <p className="text-white font-semibold">{ratingsData.average_music || 'N/A'}</p>
            </div>
          </div>
        </div>
      )}

      {/* Reviews List */}
      <div className="space-y-3 max-h-96 overflow-y-auto">
        {(!ratingsData || ratingsData.ratings?.length === 0) ? (
          <div className="text-center py-8 text-slate-400">
            <Star className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>No reviews yet. Be the first to review!</p>
          </div>
        ) : (
          ratingsData.ratings?.map((review) => (
            <div key={review.id} className="p-3 bg-slate-800/30 rounded-lg border border-slate-700/50">
              <div className="flex items-start gap-3">
                <Avatar className="w-8 h-8">
                  {review.user?.profile_picture_url ? (
                    <AvatarImage src={`${process.env.REACT_APP_BACKEND_URL}${review.user.profile_picture_url}`} />
                  ) : null}
                  <AvatarFallback className="bg-purple-600 text-white text-xs">
                    {review.user?.username?.[0]?.toUpperCase() || 'U'}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <p className="font-semibold text-white text-sm">{review.user?.username}</p>
                    <span className="text-xs text-slate-500">
                      {formatDistanceToNow(new Date(review.created_at), { addSuffix: true })}
                    </span>
                  </div>
                  <StarRating value={review.rating} readonly size="sm" />
                  {review.review && (
                    <p className="text-sm text-slate-300 mt-2">{review.review}</p>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </Card>
  );
};

export default BarRatings;
